import { Checkmark } from '@carbon/icons-react';
import { HoverView, Pad, PadBox } from 'component/basics';
import { CTAButton } from 'component/button';
import {
    colorBorderFacepile,
    colorBorderPrimary,
    colorButtonAccent,
    colorTagAccentLight,
    colorTextAccent,
    colorTextPrimary,
    colorTextSecondary,
} from 'component/color';
import type { CommentConfig } from 'component/comment';
import { Checkbox } from 'component/form';
import { Modal } from 'component/modal';
import { Heading, UtilityText } from 'component/text';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useDatastore, useLogEvent, useSessionData } from 'util/datastore';
import { logEventAsync, useStartTime } from 'util/eventlog';
import { defineModuleGlobal, defineModuleMap } from 'util/moduledata';
import type { StandardConfig } from 'util/stdconfig';
import type { Feature } from 'util/stdtypes';
import { t } from 'util/stdtypes';
import { toPressColor, translucentPressColor } from 'util/util-color';

export const SatisfactionSurveyFeature: Feature<StandardConfig, {}> = {
    name: 'Survey Satisfaction',
    key: 'satisfaction_survey',
    dev: true,
    providesConfig: {
        sidebarCloseModal: SatisfactionSurveyModal,
        sidebarShouldShowCloseModal: shouldShowSatisfactionSurvey,
    },
};

export const UsabilitySurveyFeature: Feature<StandardConfig & CommentConfig, {}> = {
    name: 'Survey Usability',
    key: 'usability_survey',
    dev: true,
    providesConfig: {
        commentPostTriggers: [maybeEnableUsabilitySurveyAfterTopLevelCommentAsync],
        pageTopRightWidgets: [ShowUsabilitySurveyIfEnabled],
    },
};

const usabilityLabels = [t('Strongly disagree'), t('Disagree'), t('Neither agree nor disagree'), t('Agree'), t('Strongly agree')];

const moduleGlobalLastShowedUsabilitySurvey = defineModuleGlobal<number>('USER_GLOBAL', 'survey', 'lastShowedUsabilitySurvey');

export async function maybeEnableUsabilitySurveyAfterTopLevelCommentAsync({ datastore, comment }) {
    if (comment.replyTo) {
        return;
    }
    const lastShowedUsabilitySurvey = (await datastore.getModuleGlobalAsync(moduleGlobalLastShowedUsabilitySurvey)) ?? 0;
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    if (lastShowedUsabilitySurvey < oneWeekAgo) {
        datastore.setModuleGlobalAsync(moduleGlobalLastShowedUsabilitySurvey, Date.now());
        datastore.setSessionData('usabilitySurveyEnabled', true);
    }
}

export function ShowUsabilitySurveyIfEnabled() {
    const enabled = useSessionData('usabilitySurveyEnabled');
    const datastore = useDatastore();

    function onDone() {
        datastore.setSessionData('usabilitySurveyEnabled', false);
    }

    return enabled ? (
        <Modal onClose={onDone}>
            <PadBox horiz={20} vert={40}>
                <UsabilitySurveyModal onDone={onDone} />
            </PadBox>
        </Modal>
    ) : null;
}

export function UsabilitySurveyModal({ onDone }) {
    const [easyToUse, setEasyToUse] = useState(null);
    const [meetsNeeds, setMeetsNeeds] = useState(null);

    const datastore = useDatastore();
    const startTime = useStartTime();

    async function onSubmit() {
        await logEventAsync(datastore, 'survey-usability', {
            easyToUse,
            meetsNeeds,
            timeTaken: Date.now() - startTime,
        });
        onDone && onDone();
    }

    return (
        <View>
            <Heading label={t('Please indicate how much you agree or disagree with each statement')} type='large' />
            <Pad size={8} />
            <UtilityText color={colorTextSecondary} label={t('Share your feedback to help improve our Beta experience.')} type='large' />
            <Pad size={32} />
            <UtilityText label={t('This conversation platform is easy to use')} type='small' weight='medium' />
            <Pad size={12} />
            <View testID='survey-easy-to-use'>
                <SurveyOptionList onChange={setEasyToUse} optionLabels={usabilityLabels} value={easyToUse} />
            </View>
            <Pad size={32} />
            <UtilityText label={t('This conversation platform meets my needs')} type='small' weight='medium' />
            <Pad size={12} />
            <View testID='survey-meets-needs'>
                <SurveyOptionList onChange={setMeetsNeeds} optionLabels={usabilityLabels} value={meetsNeeds} />
            </View>
            <Pad size={32} />
            <CTAButton label={t('Submit')} onPress={onSubmit} type='accent' wide />
        </View>
    );
}

const satisfactionLabels = [
    t('Very unsatisfied'),
    t('Unsatisfied'),
    t('Neither satisfied nor unsatisfied'),
    t('Satisfied'),
    t('Very satisfied'),
];

const global_app_start_time = Date.now();

const thirtySeconds = 1000 * 30;

function shouldShowSatisfactionSurvey() {
    const appOpenMoreThanThirtySecondsAgo = Date.now() - global_app_start_time > thirtySeconds;
    const random25Percent = Math.random() < 0.25;
    return appOpenMoreThanThirtySecondsAgo && random25Percent;
}

export function SatisfactionSurveyModal({ onDone }) {
    const [value, setValue] = useState(null);
    const [selectedInfluences, setSelectedInfluences] = useState({
        qualityComments: false,
        easeOfUse: false,
        interestTopic: false,
        loginRequirement: false,
        qualityQuestion: false,
    });
    useLogEvent('show-survey-satisfaction');
    const datastore = useDatastore();
    const startTime = useStartTime();

    async function onSubmit() {
        await logEventAsync(datastore, 'survey-satisfaction', {
            satisfaction: value,
            influences: Object.keys(selectedInfluences).filter((key) => selectedInfluences[key]),
            timeTakenForSurvey: Date.now() - startTime,
            timeSinceAppStart: Date.now() - global_app_start_time,
        });
        datastore.setSessionData('surveyShown', true);
        onDone && onDone();
    }

    return (
        <View>
            <Heading label={t("We'd love your feedback!")} type='large' />
            <Pad size={8} />
            <UtilityText color={colorTextSecondary} label={t('Share your feedback to help improve our Beta experience.')} type='large' />
            <Pad size={32} />
            <UtilityText label={t('How satisfied are you with this conversation platform?')} type='small' weight='medium' />
            <Pad size={12} />
            <SurveyOptionList onChange={setValue} optionLabels={satisfactionLabels} value={value} />
            <Pad size={32} />
            <UtilityText label={t('What influenced your answer? Choose all that apply.')} type='small' weight='medium' />
            <Pad size={12} />
            <CheckboxList selectedInfluences={selectedInfluences} setSelectedInfluences={setSelectedInfluences} />
            <Pad size={32} />
            <CTAButton label={t('Submit')} onPress={onSubmit} type='accent' wide />
        </View>
    );
}

export function SurveyOptionList({ optionLabels, value, onChange }) {
    return (
        <View style={{ gap: 12 }}>
            {optionLabels.map((label, index) => {
                const score = index + 1;
                return <SurveyOption key={index} label={label} onPress={() => onChange(score)} selected={value === score} />;
            })}
        </View>
    );
}

export function CheckboxList({ selectedInfluences, setSelectedInfluences }) {
    function toggleInfluence(key) {
        setSelectedInfluences((prev) => {
            const newState = { ...prev };
            newState[key] = !prev[key];
            return newState;
        });
    }

    return (
        <View>
            <Checkbox
                label={t('Quality of comments and replies')}
                onChange={() => toggleInfluence('qualityComments')}
                value={selectedInfluences.qualityComments}
            />
            <Checkbox label={t('Ease of use')} onChange={() => toggleInfluence('easeOfUse')} value={selectedInfluences.easeOfUse} />
            <Checkbox
                label={t('Interest in the topic')}
                onChange={() => toggleInfluence('interestTopic')}
                value={selectedInfluences.interestTopic}
            />
            <Checkbox
                label={t('Needing to log in to participate')}
                onChange={() => toggleInfluence('loginRequirement')}
                value={selectedInfluences.loginRequirement}
            />
            <Checkbox
                label={t('Quality of the question guiding the conversation')}
                onChange={() => toggleInfluence('qualityQuestion')}
                value={selectedInfluences.qualityQuestion}
            />
        </View>
    );
}

export function SurveyOption({ selected, label, onPress }) {
    const s = SurveyOptionStyle;
    return (
        <HoverView
            ariaLabel={label}
            hoverStyle={!selected ? s.hover : {}}
            onPress={onPress}
            pressedStyle={!selected ? s.pressed : {}}
            style={selected ? s.selected : s.unselected}
        >
            <UtilityText color={selected ? colorTextAccent : colorTextPrimary} label={label} type='small' />
            <View style={selected ? s.selectedCircle : s.circle}>
                {selected && <Checkmark color={selected ? colorBorderFacepile : colorBorderPrimary} size={14} />}
            </View>
        </HoverView>
    );
}
const SurveyOptionStyle = StyleSheet.create({
    selectedCircle: {
        width: 24,
        height: 24,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colorButtonAccent,
    },
    circle: {
        width: 24,
        height: 24,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    unselected: {
        padding: 8,
        paddingLeft: 12,
        borderRadius: 32,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    selected: {
        padding: 8,
        paddingLeft: 12,
        borderRadius: 32,
        borderWidth: 1,
        borderColor: translucentPressColor(),
        backgroundColor: colorTagAccentLight,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    hover: {
        backgroundColor: translucentPressColor(),
    },
    pressed: {
        backgroundColor: colorBorderPrimary,
    },
});
